import { useMemo } from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';

// ─── Types ──────────────────────────────────────────────────────────
export interface SupporterEntry {
  userId: string;
  displayName: string;
  iconUrl: string | null;
  totalAmount: number;
}

export interface TopSupportersProps {
  data: SupporterEntry[];
}

// ─── Constants ──────────────────────────────────────────────────────
const MAX_DISPLAY = 10;

const RANK_BADGES: Record<number, { emoji: string; border: string; bg: string }> = {
  1: { emoji: '🥇', border: 'border-yellow-400/40', bg: 'bg-yellow-400/5' },
  2: { emoji: '🥈', border: 'border-gray-300/30', bg: 'bg-gray-300/5' },
  3: { emoji: '🥉', border: 'border-amber-700/30', bg: 'bg-amber-700/5' },
};

// ─── Helpers ────────────────────────────────────────────────────────
function getInitial(name: string): string {
  if (!name) return '?';
  // Handle Japanese & Latin names
  return name.charAt(0).toUpperCase();
}

// Avatar gradient colors by rank for top 3
const AVATAR_RING_COLORS: Record<number, string> = {
  1: 'ring-yellow-400',
  2: 'ring-gray-300',
  3: 'ring-amber-600',
};

// ─── Item animation variants ────────────────────────────────────────
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.06,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -16 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.35, ease: 'easeOut' as const },
  },
};

// ─── Component ──────────────────────────────────────────────────────
export const TopSupporters = ({ data }: TopSupportersProps) => {
  const sorted = useMemo(
    () =>
      [...data]
        .sort((a, b) => b.totalAmount - a.totalAmount)
        .slice(0, MAX_DISPLAY),
    [data],
  );

  if (sorted.length === 0) {
    return (
      <div className="rounded-xl border border-white/10 bg-black/30 p-5">
        <h3 className="mb-4 text-sm font-bold tracking-widest text-gray-400">
          トップサポーター
        </h3>
        <p className="py-8 text-center text-xs text-gray-600">
          まだサポーターがいません
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="w-full"
    >
      <div className="rounded-xl border border-white/10 bg-black/30 p-5">
        <h3 className="mb-4 text-sm font-bold tracking-widest text-gray-400">
          トップサポーター
        </h3>

        <motion.ul
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-1"
        >
          {sorted.map((supporter, idx) => {
            const rank = idx + 1;
            const badge = RANK_BADGES[rank];
            const ringColor = AVATAR_RING_COLORS[rank];

            return (
              <motion.li
                key={supporter.userId}
                variants={itemVariants}
                className={clsx(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-white/5',
                  badge?.bg,
                  badge?.border ? `border ${badge.border}` : 'border border-transparent',
                )}
              >
                {/* Rank */}
                <div className="w-7 flex-shrink-0 text-center">
                  {badge ? (
                    <span className="text-lg">{badge.emoji}</span>
                  ) : (
                    <span className="text-xs font-bold text-gray-500">
                      {rank}
                    </span>
                  )}
                </div>

                {/* Avatar */}
                <div
                  className={clsx(
                    'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full overflow-hidden',
                    ringColor ? `ring-2 ${ringColor}` : 'ring-1 ring-white/10',
                  )}
                >
                  {supporter.iconUrl ? (
                    <img
                      src={supporter.iconUrl}
                      alt={supporter.displayName}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gray-800 text-sm font-bold text-gray-300">
                      {getInitial(supporter.displayName)}
                    </div>
                  )}
                </div>

                {/* Name */}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-white">
                    {supporter.displayName}
                  </p>
                </div>

                {/* Amount */}
                <div className="flex-shrink-0 text-right">
                  <span
                    className={clsx(
                      'text-sm font-bold font-display',
                      rank === 1
                        ? 'text-yellow-400'
                        : rank === 2
                          ? 'text-gray-300'
                          : rank === 3
                            ? 'text-amber-600'
                            : 'text-neon-blue',
                    )}
                  >
                    ¥{supporter.totalAmount.toLocaleString()}
                  </span>
                </div>
              </motion.li>
            );
          })}
        </motion.ul>
      </div>
    </motion.div>
  );
};
